'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
  badgeVariant?: 'danger' | 'warning' | 'info';
  minRole?: 'tecnico' | 'admin' | 'super_admin';
}

interface NavGroup {
  heading: string;
  items: NavItem[];
  minRole?: 'tecnico' | 'admin' | 'super_admin';
}

const navGroups: NavGroup[] = [
  {
    heading: 'OPERAZIONI',
    items: [
      { label: 'Pannello di Controllo', href: '/network-dashboard', icon: 'SquaresPlusIcon' },
      { label: 'System Health Monitor', href: '/system-health', icon: 'HeartIcon' },
      { label: 'Indirizzi IP', href: '/ip-address-management', icon: 'GlobeAltIcon' },
      { label: 'Inventario Dispositivi', href: '/device-inventory', icon: 'ServerIcon' },
      { label: 'Mappa di Rete', href: '/network-topology', icon: 'ShareIcon' },
    ],
  },
  {
    heading: 'TICKET E MANUTENZIONE',
    items: [
      { label: 'Gestione Ticket', href: '/tickets', icon: 'TicketIcon' },
      { label: 'Manutenzione e Change', href: '/manutenzione', icon: 'WrenchScrewdriverIcon' },
    ],
  },
  {
    heading: 'ASSET MANAGEMENT',
    items: [
      { label: 'Asset e Licenze', href: '/asset-management', icon: 'CubeIcon' },
      { label: 'QR Code e Firma Digitale', href: '/qr-firma', icon: 'QrCodeIcon' },
    ],
  },
  {
    heading: 'REPARTI E ORGANIZZAZIONE',
    items: [
      { label: 'Reparti e Sedi', href: '/reparti', icon: 'BuildingOffice2Icon' },
      { label: 'Mappa Planimetrica', href: '/mappa-planimetrica', icon: 'MapIcon' },
      { label: 'Fornitori', href: '/fornitori', icon: 'BuildingStorefrontIcon' },
      { label: 'Calendario Scadenze', href: '/scadenze', icon: 'CalendarIcon' },
      { label: 'Visitatori Tecnici', href: '/visitatori', icon: 'UserGroupIcon' },
      { label: 'Pool e Prestiti', href: '/pool-dispositivi', icon: 'ArrowsRightLeftIcon' },
    ],
  },
  {
    heading: 'INFRASTRUTTURA',
    items: [
      { label: 'Sottoreti e VLAN', href: '/subnet-vlan', icon: 'CircleStackIcon' },
    ],
  },
  {
    heading: 'TOPOLOGIA E RETE',
    items: [
      { label: 'Porte Switch', href: '/switch-ports', icon: 'RectangleGroupIcon' },
      { label: 'DNS Interni', href: '/dns-management', icon: 'GlobeAltIcon' },
      { label: 'Utilizzo Banda', href: '/bandwidth', icon: 'ChartBarIcon' },
      { label: 'Network Discovery', href: '/network-discovery', icon: 'MagnifyingGlassIcon' },
      { label: 'Gestione MAC', href: '/mac-management', icon: 'FingerPrintIcon' },
      { label: 'Storico Topologia', href: '/topology-snapshots', icon: 'CameraIcon' },
      { label: 'Diagramma Rack', href: '/rack-diagram', icon: 'ServerStackIcon' },
      { label: 'Cablaggio', href: '/cablaggio', icon: 'LinkIcon' },
      { label: 'Circuiti WAN', href: '/wan-circuits', icon: 'SignalIcon' },
      { label: 'Test Velocità', href: '/speed-test', icon: 'BoltIcon' },
    ],
  },
  {
    heading: 'GUIDE E SUPPORTO',
    items: [
      { label: 'Guide Dispositivi', href: '/guide', icon: 'BookOpenIcon' },
      { label: 'Storico Configurazioni', href: '/storico-configurazioni', icon: 'ClockIcon' },
      { label: 'Chatbot Interno', href: '/chatbot', icon: 'CpuChipIcon' },
    ],
  },
  {
    heading: 'MONITORAGGIO AMBIENTALE',
    items: [
      { label: 'Dashboard Ambientale', href: '/monitoraggio-ambientale', icon: 'SunIcon' },
      { label: 'Sensori Sala Server', href: '/sensori-sala-server', icon: 'SignalIcon' },
      { label: 'UPS e Alimentazione', href: '/ups-alimentazione', icon: 'BoltIcon' },
      { label: 'Consumi Energetici', href: '/consumi-energetici', icon: 'ChartBarIcon' },
      { label: 'Climatizzatori CED', href: '/climatizzatori-ced', icon: 'CloudIcon' },
      { label: 'Accessi Fisici', href: '/accessi-fisici', icon: 'KeyIcon' },
      { label: 'Integrazione BMS', href: '/bms-integrazione', icon: 'CpuChipIcon' },
    ],
  },
  {
    heading: 'ALLARMI E NOTIFICHE',
    items: [
      { label: 'Dashboard Allarmi', href: '/allarmi', icon: 'BellAlertIcon' },
      { label: 'Alert Twilio SMS', href: '/twilio-alerts', icon: 'DevicePhoneMobileIcon' },
      { label: 'Notifiche e Regole', href: '/notifiche', icon: 'BellIcon' },
      { label: 'Turni Reperibilità', href: '/turni-reperibilita', icon: 'CalendarDaysIcon' },
      { label: 'Storico Eventi', href: '/storico-eventi', icon: 'ClockIcon' },
    ],
  },
  {
    heading: 'ADMIN NOTIFICHE',
    minRole: 'admin',
    items: [
      { label: 'Regole Notifica', href: '/admin/notifiche/regole', icon: 'AdjustmentsHorizontalIcon' },
      { label: 'Canali (Email/SMS)', href: '/admin/notifiche/canali', icon: 'SignalIcon' },
      { label: 'Destinatari', href: '/admin/notifiche/destinatari', icon: 'UsersIcon' },
      { label: 'Soglie Allarme', href: '/admin/notifiche/soglie', icon: 'ChartBarIcon' },
      { label: 'Template', href: '/admin/notifiche/template', icon: 'DocumentTextIcon' },
      { label: 'Orari No-Disturb', href: '/admin/notifiche/orari', icon: 'ClockIcon' },
      { label: 'Log Notifiche', href: '/admin/notifiche/log', icon: 'QueueListIcon' },
      { label: 'Test Notifiche', href: '/admin/notifiche/test', icon: 'PaperAirplaneIcon' },
      { label: 'Test Canali Unificato', href: '/admin/notifiche/test-canali', icon: 'BeakerIcon' },
    ],
  },
  {
    heading: 'UTENTI E ACCESSI',
    minRole: 'admin',
    items: [
      { label: 'Gestione Utenti', href: '/gestione-utenti', icon: 'UsersIcon' },
      { label: 'Sessioni Attive', href: '/admin/sessions', icon: 'ComputerDesktopIcon' },
      { label: 'API Keys', href: '/admin/api-keys', icon: 'KeyIcon' },
      { label: 'Audit Cambio Ruolo', href: '/admin/audit-roles', icon: 'ShieldCheckIcon' },
      { label: 'Agenti Monitoraggio', href: '/admin/agent-config', icon: 'CpuChipIcon' },
      { label: 'Pannello Admin', href: '/admin', icon: 'Cog8ToothIcon' },
      { label: 'Strutture', href: '/admin/strutture', icon: 'BuildingOffice2Icon', minRole: 'super_admin' },
      { label: 'Richieste Strutture', href: '/admin/richieste-strutture', icon: 'InboxIcon', minRole: 'super_admin' },
    ],
  },
  {
    heading: 'REPORT E COMPLIANCE',
    items: [
      { label: 'Report e KPI', href: '/report-kpi', icon: 'ChartPieIcon' },
      { label: 'Report Condivisi', href: '/report-condivisi', icon: 'ShareIcon' },
      { label: 'Report PDF con Logo', href: '/pdf-report', icon: 'DocumentArrowDownIcon' },
      { label: 'Dashboard Executive', href: '/executive-dashboard', icon: 'PresentationChartLineIcon' },
      { label: 'Analisi Costi', href: '/centri-costo', icon: 'CurrencyEuroIcon' },
    ],
  },
  {
    heading: 'SICUREZZA',
    items: [
      { label: 'Sicurezza e Backup', href: '/sicurezza', icon: 'ShieldCheckIcon' },
    ],
  },
  {
    heading: 'INTEGRAZIONI',
    minRole: 'admin',
    items: [
      { label: 'Integrazioni Esterne', href: '/integrazioni', icon: 'ArrowPathRoundedSquareIcon' },
      { label: 'Teams / Slack / Calendar', href: '/integrazioni-esterne', icon: 'ChatBubbleLeftRightIcon' },
    ],
  },
  {
    heading: 'KNOWLEDGE BASE',
    items: [
      { label: 'Knowledge Base', href: '/knowledge-base', icon: 'BookOpenIcon' },
    ],
  },
  {
    heading: 'AUTOMAZIONE AI',
    minRole: 'admin',
    items: [
      { label: 'Automazione e AI', href: '/automazione', icon: 'CpuChipIcon' },
      { label: 'Regole If/Then', href: '/automazione-regole', icon: 'BoltIcon' },
    ],
  },
  {
    heading: 'BUSINESS',
    items: [
      { label: 'Piani e Prezzi', href: '/prezzi', icon: 'CurrencyEuroIcon' },
      { label: 'Il Mio Abbonamento', href: '/abbonamento', icon: 'CreditCardIcon' },
      { label: 'Programma Referral', href: '/referral', icon: 'GiftIcon' },
    ],
  },
  {
    heading: 'IMPOSTAZIONI',
    items: [
      { label: 'Impostazioni e PWA', href: '/impostazioni', icon: 'Cog6ToothIcon' },
      { label: 'Sicurezza e Privacy', href: '/sicurezza', icon: 'ShieldCheckIcon' },
    ],
  },
];

const badgeColors = {
  danger: 'bg-red-500/20 text-red-400',
  warning: 'bg-amber-500/20 text-amber-400',
  info: 'bg-sky-500/20 text-sky-400',
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { profile, signOut, roleLabel, canAdmin, canWrite } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      router.push('/login');
    } catch {
      setSigningOut(false);
    }
  };

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : profile?.email?.slice(0, 2).toUpperCase() || 'UT';

  const isAdmin = canAdmin();
  const isTecnico = canWrite();

  const isGroupVisible = (group: NavGroup): boolean => {
    if (!group.minRole) return true;
    if (group.minRole === 'super_admin') return profile?.role === 'super_admin';
    if (group.minRole === 'admin') return isAdmin;
    if (group.minRole === 'tecnico') return isTecnico;
    return true;
  };

  const isItemVisible = (item: NavItem): boolean => {
    if (!item.minRole) return true;
    if (item.minRole === 'super_admin') return profile?.role === 'super_admin';
    if (item.minRole === 'admin') return isAdmin;
    if (item.minRole === 'tecnico') return isTecnico;
    return true;
  };

  const visibleGroups = navGroups
    .filter(isGroupVisible)
    .map(group => ({
      ...group,
      items: group.items.filter(isItemVisible),
    }))
    .filter(group => group.items.length > 0);

  return (
    <aside
      className={`
        flex flex-col h-full bg-surface border-r border-border
        transition-all duration-300 ease-in-out overflow-hidden
        ${collapsed ? 'w-16' : 'w-64'}
      `}
    >
      {/* Header */}
      <div className={`flex items-center h-16 border-b border-border flex-shrink-0 ${collapsed ? 'justify-center px-0' : 'px-4 gap-2'}`}>
        {collapsed ? (
          <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors">
            <AppLogo size={30} />
          </button>
        ) : (
          <>
            <AppLogo size={30} />
            <span className="font-semibold text-lg text-white tracking-tight">GestioneLab</span>
            <button
              onClick={onToggle}
              className="ml-auto p-1 rounded-md hover:bg-surface-elevated transition-colors"
              aria-label="Comprimi menu"
            >
              <Icon name="ChevronLeftIcon" size={18} className="text-muted-color" />
            </button>
          </>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2 space-y-5">
        {visibleGroups.map((group) => (
          <div key={`group-${group.heading}`}>
            {!collapsed && (
              <p className="text-xs font-bold text-muted-color tracking-widest px-2 mb-2">
                {group.heading}
              </p>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => {
                const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
                return (
                  <li key={`nav-${item.href}-${item.label}`}>
                    <Link
                      href={item.href}
                      title={collapsed ? item.label : undefined}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold
                        transition-all duration-150 group relative
                        ${isActive
                          ? 'bg-sky-500/10 text-sky-400' : 'text-secondary-color hover:bg-surface-elevated hover:text-white'
                        }
                      `}
                    >
                      <Icon
                        name={item.icon as any}
                        size={18}
                        className={`flex-shrink-0 ${isActive ? 'text-sky-400' : 'text-muted-color group-hover:text-white'}`}
                      />
                      {!collapsed && (
                        <span className="truncate">{item.label}</span>
                      )}
                      {!collapsed && item.badge !== undefined && item.badge > 0 && (
                        <span className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full ${badgeColors[item.badgeVariant || 'info']}`}>
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* User Footer */}
      <div className={`border-t border-border flex-shrink-0 ${collapsed ? 'p-2' : 'p-3'}`}>
        {!collapsed ? (
          <div className="space-y-2">
            <Link
              href="/profile"
              className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-surface-elevated transition-colors group"
            >
              <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-sky-400">{initials}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{profile?.full_name || 'Utente'}</p>
                <p className="text-xs text-muted-color truncate">{roleLabel()}</p>
              </div>
              <Icon name="UserCircleIcon" size={16} className="text-muted-color group-hover:text-white flex-shrink-0" />
            </Link>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-muted-color hover:bg-red-500/10 hover:text-red-400 transition-colors"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={16} />
              {signingOut ? 'Uscita...' : 'Esci'}
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Link href="/profile" title="Profilo">
              <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center cursor-pointer hover:bg-sky-500/30 transition-colors">
                <span className="text-xs font-bold text-sky-400">{initials}</span>
              </div>
            </Link>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              title="Esci"
              className="p-1.5 rounded-lg text-muted-color hover:bg-red-500/10 hover:text-red-400 transition-colors"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={16} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}